/**
 * GameContext - Central state management for the 3D game world
 * 
 * Tracks player position, current scene, travel method, and provides
 * methods for scene transitions and fast travel.
 */

import { createContext, useContext, useState, useCallback, useRef } from 'react';

// Travel methods - extensible for future vehicles/mounts
export const TRAVEL_METHODS = {
  WALK: 'walk',
  FAST_TRAVEL: 'fastTravel',
  // Future: HORSE: 'horse', CART: 'cart', SHIP: 'ship', etc.
};

// Default player state
const DEFAULT_PLAYER_STATE = {
  // World hierarchy position
  realmId: 'midgard',
  regionId: 'central-plains',
  settlementId: 'thornhaven',
  sceneId: 'thornhaven-exterior',
  currentLocationId: null, // null when outdoors, set when inside a building
  
  // 3D position within current scene
  position: { x: 0, y: 0, z: 10 },
  rotation: { x: 0, y: 0, z: 0 },
  
  // Movement state
  isMoving: false,
  isSprinting: false,
  
  // Travel
  travelMethod: TRAVEL_METHODS.WALK,
  isTransitioning: false, // True during scene transitions
};

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const [playerState, setPlayerState] = useState(DEFAULT_PLAYER_STATE);
  const [isGameActive, setIsGameActive] = useState(false);
  const playerRef = useRef(null); // Reference to the actual 3D player controller
  
  // Position ref for real-time tracking (doesn't cause re-renders)
  const positionRef = useRef({ x: 0, y: 1, z: 10 });
  
  // Update player position (called by PlayerController) - only updates ref, not state
  const updatePosition = useCallback((position, rotation) => {
    positionRef.current = { ...position };
    // Don't update React state - this would cause infinite re-renders
  }, []);
  
  // Get current position (from ref)
  const getPosition = useCallback(() => {
    return positionRef.current;
  }, []);
  
  // Update movement state
  const updateMovementState = useCallback((isMoving, isSprinting = false) => {
    setPlayerState(prev => ({
      ...prev,
      isMoving,
      isSprinting,
    }));
  }, []);
  
  // Enter a location (building interior)
  const enterLocation = useCallback((locationId, sceneId, spawnPoint = 'entrance') => {
    setPlayerState(prev => ({
      ...prev,
      currentLocationId: locationId,
      sceneId,
      position: { ...spawnPoint.position },
      rotation: spawnPoint.rotation ? { ...spawnPoint.rotation } : prev.rotation,
      travelMethod: TRAVEL_METHODS.WALK,
      isTransitioning: true,
    }));
    
    // Clear transition flag after brief delay
    setTimeout(() => {
      setPlayerState(prev => ({ ...prev, isTransitioning: false }));
    }, 500);
  }, []);
  
  // Exit a location (return to exterior)
  const exitLocation = useCallback((sceneId, spawnPoint) => {
    setPlayerState(prev => ({
      ...prev,
      currentLocationId: null,
      sceneId,
      position: { ...spawnPoint.position },
      rotation: spawnPoint.rotation ? { ...spawnPoint.rotation } : prev.rotation,
      travelMethod: TRAVEL_METHODS.WALK,
      isTransitioning: true,
    }));
    
    setTimeout(() => {
      setPlayerState(prev => ({ ...prev, isTransitioning: false }));
    }, 500);
  }, []);
  
  // Fast travel to a destination
  const fastTravel = useCallback(({ realmId, regionId, settlementId, sceneId, locationId, spawnPoint }) => {
    setPlayerState(prev => ({
      ...prev,
      realmId: realmId || prev.realmId,
      regionId: regionId || prev.regionId,
      settlementId: settlementId || prev.settlementId,
      sceneId,
      currentLocationId: locationId || null,
      position: { ...spawnPoint.position },
      rotation: spawnPoint.rotation ? { ...spawnPoint.rotation } : { x: 0, y: 0, z: 0 },
      travelMethod: TRAVEL_METHODS.FAST_TRAVEL,
      isTransitioning: true,
    }));
    
    setTimeout(() => {
      setPlayerState(prev => ({ ...prev, isTransitioning: false }));
    }, 1000); // Slightly longer for fast travel "loading"
  }, []);
  
  // Enter the game world (from map/travel UI)
  const enterGame = useCallback(({ settlementId, sceneId, locationId, spawnPoint, travelMethod = TRAVEL_METHODS.WALK }) => {
    setPlayerState(prev => ({
      ...prev,
      settlementId: settlementId || prev.settlementId,
      sceneId,
      currentLocationId: locationId || null,
      position: spawnPoint?.position || DEFAULT_PLAYER_STATE.position,
      rotation: spawnPoint?.rotation || DEFAULT_PLAYER_STATE.rotation,
      travelMethod,
      isTransitioning: false,
    }));
    setIsGameActive(true);
  }, []);
  
  // Exit the game (return to map/travel UI)
  const exitGame = useCallback(() => {
    setIsGameActive(false);
  }, []);
  
  // Reset to default state
  const resetPlayerState = useCallback(() => {
    setPlayerState(DEFAULT_PLAYER_STATE);
  }, []);
  
  const value = {
    // State
    playerState,
    isGameActive,
    playerRef,
    
    // Position ref (for real-time access without re-renders)
    positionRef,
    getPosition,
    
    // Actions
    updatePosition,
    updateMovementState,
    enterLocation,
    exitLocation,
    fastTravel,
    enterGame,
    exitGame,
    resetPlayerState,
    setPlayerState,
  };
  
  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

export default GameContext;
